import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { useAuth } from '@/auth/AuthProvider'
import { useTheme } from '@/theme/ThemeProvider'
import { Ionicon } from '@/components/ui/Ionicon'
import { AddFoodSheet } from '@/components/log/AddFoodSheet'
import { AnimatedTabBar, tabIndexForPath } from '@/components/navigation/AnimatedTabBar'
import { TAB_IMAGES, TAB_ICON_SIZE, INACTIVE_ICON_OPACITY, TAB_LABEL_KEYS } from '@/constants/icons'
import { QUICK_ADD_IMAGES } from '@/constants/meal-icons'
import { Colors } from '@/theme/tokens'
import { withAlpha } from '@/theme/color'
import type { MealTime } from '@/types'

/**
 * App shell — web port of the mobile tab navigator (app/(tabs)/_layout.tsx +
 * components/navigation/AnimatedTabBar.tsx).
 *
 * - Phone width (< md): no top bar (every mobile screen draws its own large
 *   title header); the floating AnimatedTabBar with the centre "+" on the
 *   four tab screens.
 * - Desktop (md+): a left sidebar with the same illustrated tab art, labels
 *   and faint focus pill, plus Saved Foods (web-only nav entry) and the
 *   theme / account footer.
 * - Content: every route renders in ONE centered column capped at
 *   Layout.contentMaxWidth (600px, `max-w-content`) — the cap the app uses on
 *   iPad. The column adds NO side/top padding: each screen ports its own
 *   mobile margins (cards `mx-md`, headers `px-lg`). On phone width it adds
 *   the bottom padding that clears the floating tab bar.
 *
 * The Add Food sheet lives here (one instance) so the "+" works from any tab;
 * screens open it with `useAddFood().openAddFood(mealType?)`.
 */

interface AddFoodContextValue {
  /** Open the Add Food sheet, optionally pre-targeting a meal. */
  openAddFood: (mealType?: MealTime) => void
  closeAddFood: () => void
  isAddFoodOpen: boolean
}

const AddFoodContext = createContext<AddFoodContextValue | null>(null)

export function useAddFood(): AddFoodContextValue {
  const ctx = useContext(AddFoodContext)
  if (!ctx) throw new Error('useAddFood must be used within AppShell')
  return ctx
}

type SidebarItem = {
  to: string
  /** Paths that light this item up (stack screens count for their tab). */
  match: string[]
  image: string
  labelKey: string
}

// Tab order of the app (Diary, Recipes, Progress, Settings); Saved Foods is
// web-only nav (the app reaches it from the Add Food sheet) and sits after
// Recipes, with the Quick Add "Saved" art.
const SIDEBAR: SidebarItem[] = [
  { to: '/diary', match: ['/diary', '/log', '/meal-summary', '/weight', '/blood-pressure'], image: TAB_IMAGES.index, labelKey: TAB_LABEL_KEYS.index },
  { to: '/recipes', match: ['/recipes'], image: TAB_IMAGES.recipes, labelKey: TAB_LABEL_KEYS.recipes },
  { to: '/saved-foods', match: ['/saved-foods'], image: QUICK_ADD_IMAGES.savedFoods, labelKey: 'savedFoods.title' },
  { to: '/progress', match: ['/progress', '/history', '/report'], image: TAB_IMAGES.progress, labelKey: TAB_LABEL_KEYS.progress },
  { to: '/settings', match: ['/settings'], image: TAB_IMAGES.settings, labelKey: TAB_LABEL_KEYS.settings },
]

function isItemActive(item: SidebarItem, pathname: string): boolean {
  return item.match.some((p) => pathname === p || pathname.startsWith(p + '/'))
}

const CENTER_FACE_SHADOW = `0px 4px 4px ${withAlpha(Colors.light.primaryDark, 0.15)}`

export function AppShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { user, signOut } = useAuth()
  const { colorScheme, setPreference } = useTheme()

  const [addOpen, setAddOpen] = useState(false)
  const [addMealType, setAddMealType] = useState<MealTime | undefined>(undefined)

  const openAddFood = useCallback((mealType?: MealTime) => {
    setAddMealType(mealType)
    setAddOpen(true)
  }, [])
  const closeAddFood = useCallback(() => setAddOpen(false), [])
  const addFood = useMemo(
    () => ({ openAddFood, closeAddFood, isAddFoodOpen: addOpen }),
    [openAddFood, closeAddFood, addOpen],
  )

  // Centre "+" / sidebar Add Food — mobile toggles: open → close; closed →
  // jump to the Diary tab, then open with no meal pre-selected.
  const handleCenterPress = useCallback(() => {
    if (addOpen) {
      setAddOpen(false)
      return
    }
    if (tabIndexForPath(pathname) !== 0) navigate('/diary')
    openAddFood(undefined)
  }, [addOpen, pathname, navigate, openAddFood])

  const tabIndex = tabIndexForPath(pathname)
  const showTabBar = tabIndex >= 0

  return (
    <AddFoodContext.Provider value={addFood}>
      <div className="flex h-full w-full flex-col bg-bg md:flex-row">
        {/* Sidebar (desktop) */}
        <aside className="no-print hidden w-64 shrink-0 flex-col border-r border-border bg-surface p-md md:flex">
          <div className="mb-lg flex items-center gap-sm px-sm pt-sm">
            <img src="/icon-192.png" alt="" className="h-9 w-9 rounded-[10px]" />
            <span className="text-[22px] font-bold leading-[28px] text-text" style={{ letterSpacing: -0.5 }}>
              Liverly
            </span>
          </div>

          <button
            type="button"
            onClick={handleCenterPress}
            aria-expanded={addOpen}
            className="mb-md flex items-center justify-center gap-sm rounded-full bg-button-primary-strong px-md py-smd text-button-primary-text transition-transform duration-150 active:scale-[0.97]"
            style={{ boxShadow: CENTER_FACE_SHADOW }}
          >
            <span
              className="flex"
              style={{ transform: `rotate(${addOpen ? 45 : 0}deg)`, transition: 'transform 300ms cubic-bezier(0.34, 1.32, 0.64, 1)' }}
            >
              <Ionicon name="add" size={22} />
            </span>
            <span className="text-[15px] font-semibold">{t('diary.addFood.title')}</span>
          </button>

          <nav className="flex flex-1 flex-col gap-xs">
            {SIDEBAR.map((item) => {
              const active = isItemActive(item, pathname)
              return (
                <button
                  key={item.to}
                  type="button"
                  onClick={() => navigate(item.to)}
                  aria-current={active ? 'page' : undefined}
                  className={clsx(
                    'flex items-center gap-smd rounded-[16px] px-smd py-sm text-left transition-colors',
                    active
                      ? 'bg-primary/[0.07] dark:bg-primary/10'
                      : 'hover:bg-surface-inner',
                  )}
                >
                  <span
                    className="flex shrink-0"
                    style={{
                      width: TAB_ICON_SIZE,
                      height: TAB_ICON_SIZE,
                      transform: `scale(${active ? 1.15 : 1})`,
                      transition: 'transform 300ms cubic-bezier(0.34, 1.32, 0.64, 1)',
                    }}
                  >
                    <img
                      src={item.image}
                      alt=""
                      aria-hidden
                      draggable={false}
                      className="select-none object-contain"
                      style={{ width: TAB_ICON_SIZE, height: TAB_ICON_SIZE, opacity: active ? 1 : INACTIVE_ICON_OPACITY }}
                    />
                  </span>
                  <span
                    className={clsx(
                      'truncate text-[15px] font-medium',
                      active ? 'text-primary' : 'text-text-secondary',
                    )}
                  >
                    {t(item.labelKey)}
                  </span>
                </button>
              )
            })}
          </nav>

          <div className="mt-md border-t border-border pt-md">
            <button
              type="button"
              onClick={() => setPreference(colorScheme === 'dark' ? 'light' : 'dark')}
              className="mb-sm flex w-full items-center gap-smd rounded-[16px] px-smd py-sm text-body-sm text-text-secondary hover:bg-surface-inner"
            >
              <Ionicon name={colorScheme === 'dark' ? 'sunny-outline' : 'moon-outline'} size={18} />
              {t('settings.theme.title')}
            </button>
            <p className="truncate px-smd text-caption text-text-muted" title={user?.email ?? ''}>
              {user?.email}
            </p>
            <button
              type="button"
              onClick={() => void signOut()}
              className="mt-xs w-full rounded-[16px] px-smd py-sm text-left text-body-sm text-text-secondary hover:bg-surface-inner hover:text-error"
            >
              {t('settings.signOut')}
            </button>
          </div>
        </aside>

        {/* Content — one centered column, max 600px, on every route */}
        <main className="relative flex-1 overflow-y-auto overflow-x-hidden">
          <div
            className={clsx(
              'mx-auto w-full max-w-content md:pb-xl',
              showTabBar && 'pb-[calc(120px+env(safe-area-inset-bottom))]',
            )}
          >
            {children}
          </div>
        </main>

        {/* Floating tab bar (phone width, tab screens only) */}
        {showTabBar ? (
          <AnimatedTabBar activeIndex={tabIndex} isAddOpen={addOpen} onCenterPress={handleCenterPress} />
        ) : null}

        <AddFoodSheet open={addOpen} onClose={closeAddFood} mealType={addMealType} />
      </div>
    </AddFoodContext.Provider>
  )
}
